import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { animate, trigger, state, style, transition } from '@angular/animations';

import { Subscription } from 'rxjs';

@Component({
    selector: 'shared-search-form',
    templateUrl: './search-form.shared.component.html',
    styleUrls: ['./search-form.shared.component.scss'],
    animations: [
        trigger('fadeOut', [
            state('in', style({
                opacity: .4,
            })),
            state('out', style({
                opacity: 0,
            })),
            transition('in => out', [
                animate('.3s')
            ])
        ])
    ]
})
export class SearchFormComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    private _deliveryAddress: APICommon.IGooglePlaceDetails = null;
    private _previousValue!: string;

    @Input() public disabled: boolean = false;
    @Input() public mode: OLO.Components.PICKUP_MODE = 'filter';
    @Input() public placeholder: string;
    @Input() public isPlaceholderVisible: boolean = true;
    @Input() public value: string;
    @Input() public enableSubmit: boolean = true;
    @Input() public enableClear: boolean = true;
    @Input() public enableClose: boolean = false;
    @Input() public open: boolean = true;
    @Input() public isFocusing: boolean = false;
    @Input() public isAutocompleteLocationsEnabled: boolean = false;

    @Input() public isDelivery: boolean = false;

    @Output() public onSearch: EventEmitter<{
        form: FormGroup;
        delivery: APICommon.IGooglePlaceDetails;
        geocoder: APICommon.IGeocoderResult;
    }> = new EventEmitter();
    @Output() public onValueChange: EventEmitter<FormGroup> = new EventEmitter();
    @Output() public onSearchReset: EventEmitter<FormGroup> = new EventEmitter();
    @Output() public onToggleOpen: EventEmitter<{ isOpen: boolean; form: FormGroup; }> = new EventEmitter();

    @ViewChild('input', { static: false }) public input: ElementRef;

    public animateOut: boolean = false;
    public clearVisible: boolean = false;
    public submitVisible: boolean = false;
    public searchForm: FormGroup;
    public valueSearch: EventEmitter<string> = new EventEmitter<string>();
    public geocoder: APICommon.IGeocoderResult;
    public searchQueryChangeListener$: Subscription;

    constructor(
        private _formBuilder: FormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.searchForm = this._formBuilder.group({
            searchInput: [
                { value: this.value, disabled: false },
                [
                    Validators.required,
                    Validators.minLength(1),
                ]
            ]
        });

        if (this.mode === 'hero') {
            this.submitVisible = true;
        }

        this.searchQueryChangeListener$ = this.searchForm?.controls?.searchInput?.valueChanges
            .subscribe(value => {
                if (this.isDelivery && this._deliveryAddress?.structured_formatting?.main_text !== value) {
                    this._deliveryAddress = null;
                }
                this.isPlaceholderVisible = false;
                this.clearVisible = false;
                this.submitVisible = true;
                this.onValueChange.emit(this.searchForm);
                this._changeDetectorRef.detectChanges();
            });
    }

    ngOnDestroy(): void {
        if (this.searchQueryChangeListener$ && !this.searchQueryChangeListener$.closed) this.searchQueryChangeListener$.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.disable) {
            if (changes.disable.currentValue === true) {
                this.searchForm.get('searchInput').disable();
            } else {
                this.searchForm.get('searchInput').enable();
            }
        }

        if (changes.value) {
            this.value = changes.value.currentValue;
        }

        if (changes.value && (changes.value.previousValue !== changes.value.currentValue && this.searchForm)) {

            setTimeout(() => {
                this.searchForm.controls.searchInput.setValue(this.value || '');
                if (!this.value) {
                    this._formRestore();

                } else {
                    this.isPlaceholderVisible = false;
                    this.clearVisible = true;
                    this.submitVisible = false;
                }

                this._changeDetectorRef.detectChanges();
            }, 50);
        }
    }

    ngAfterViewInit() {
        if (this.value) {
            this.searchForm.controls.searchInput.setValue(this.value);
            this.clearVisible = true;
            this.submitVisible = false;
        }
    }

    public inputOnBlur(): void {
        this.isFocusing = false;
        if (!this.searchForm.controls.searchInput.value) {
            this._formRestore();
        }
        this._changeDetectorRef.detectChanges();
    }

    public inputOnFocus(): void {
        this.isFocusing = true;
        this.enableTyping();
    }

    private _dropFocus(): void {
        this.isFocusing = false;
        this._changeDetectorRef.detectChanges();
    }

    private _formRestore(): void {
        this.searchForm.reset();

        this.submitVisible = false;
        this.clearVisible = false;
        this.animateOut = false;
        this.isPlaceholderVisible = true;

        if (this.mode === 'hero') {
            this.submitVisible = true;
        }

        this._changeDetectorRef.detectChanges();
    }

    public onPlaceholderClick(): void {
        if (this.disabled) return;

        this.enableTyping();
    }

    public enableTyping(): void {
        this.animateOut = true;

        if (this.input) {

            this.isPlaceholderVisible = false;
            this.submitVisible = true;

            this._changeDetectorRef.detectChanges();

            if (!this.isFocusing) {
                this.input.nativeElement.focus();
                this.isFocusing = true;
            }
        }
    }

    public onFormSubmit(): void {
        const delivery = this._deliveryAddress || null;

        this.submitVisible = false;
        this.clearVisible = true;

        this._dropFocus();
        this.onSearch.emit({
            form: this.searchForm,
            delivery,
            geocoder: this.geocoder
        });

        this._changeDetectorRef.detectChanges();
    }

    public handleAutocompleteFocus(): void {
        this._previousValue = this.searchForm.controls.searchInput.value;

        if (!this._previousValue) {
            return;
        }

        this.isFocusing = true;
        this.submitVisible = true;
        this.clearVisible = false;
        this.enableTyping();
    }

    public clearForm(): void {
        this._formRestore();
        this.onSearchReset.emit(this.searchForm);
    }

    public handleOpenClick(): void {
        if (this.open) return;
        this.open = true;
        this.onToggleOpen.emit({ isOpen: this.open, form: this.searchForm });
    }

    public handleCloseClick(): void {
        this.open = false;
        this._formRestore();
        this.onToggleOpen.emit({ isOpen: this.open, form: this.searchForm });
    }

    public handleAddressSelected($event: APICommon.IGooglePlaceDetails): void {
        this.isFocusing = false;
        this._previousValue = null;

        this.searchForm.controls.searchInput.setValue(($event?.details?.formatted_address || $event?.structured_formatting?.main_text || $event?.description || '').trim());
        this._deliveryAddress = { ...$event };
        this.geocoder = {
            name: this.searchForm.controls.searchInput.value,
            ...$event?.geometry
        };

        this.onFormSubmit();
    }

    public get deliveryFormDisabled(): boolean {
        return this.isDelivery === true && this._deliveryAddress === null;
    }

    public get isFormDisabled(): boolean {
        return this.disabled || this.searchForm.controls.searchInput.errors !== null || this.deliveryFormDisabled;
    }

    public get searchTerm(): string {
        return this.isDelivery === true || this.isAutocompleteLocationsEnabled ? this.searchForm.get('searchInput').value : null;
    }

    public get isTyping(): boolean {
        return this.isFocusing;
    }

    public get showAutocomplete(): boolean {
        return this.isDelivery || this.isAutocompleteLocationsEnabled;
    }

    public get isClearVisible(): boolean {
        return !this.submitVisible && this.clearVisible && this.enableClear;
    }
}
